/**
 * Deciding what a spreadsheet MEANS, once it has been read.
 *
 * src/import-csv.ts turns a file into headings and rows. That is the easy half.
 * This is the half where somebody else's spreadsheet becomes FlightDeck cards,
 * and it is governed by one rule Vinnie set on 19 August 2026:
 *
 *     "We never drop them, we ask the user to map them."
 *
 * So nothing here refuses a file, and nothing here silently discards a column.
 * A column FlightDeck has no field for is kept in the description under its own
 * heading unless the person says otherwise, and a word FlightDeck does not
 * recognise is a question, not a failure.
 *
 * HOW SURE A GUESS IS, IS PART OF THE GUESS. Three levels, because two would
 * lie. "Done" IS a FlightDeck status — that is not a guess, it is the same
 * word. "In Live" is our reading of somebody else's vocabulary, and it might be
 * wrong. Collapsing both into "matched" hides the one the person most needs to
 * look at, so `exact` and `guess` are kept apart all the way to the screen.
 */
import type { IssueStatus, IssuePriority, IssueKind, IssueSize } from './types';
import { parseImportDate, type DateOrder } from './when';

/** Where a column's contents end up. */
export type Field =
  | 'title' | 'description' | 'summary' | 'status' | 'priority' | 'kind'
  | 'size' | 'epic' | 'sprint' | 'assignee' | 'responsible' | 'labels' | 'blocked' | 'reference'
  | 'due' | 'start';

/**
 * '' — nobody has said yet.
 * 'note' — kept in the description under its own heading. The default for
 *          anything FlightDeck has no home for, per the rule above.
 * 'skip' — deliberately left out. Only ever chosen by a person.
 */
export type Destination = '' | Field | 'note' | 'skip';

export type Sureness = 'exact' | 'guess' | '';

export const FIELD_LABEL: Record<Field, string> = {
  title: 'Title', description: 'Description', summary: 'Plain English',
  status: 'Status', priority: 'Priority', kind: 'Task or bug', size: 'Size',
  epic: 'Epic', sprint: 'Sprint', assignee: 'Accountable', responsible: 'Responsible', labels: 'Labels',
  blocked: 'Blocked — the reason', reference: 'Their own reference',
  due: 'Due date', start: 'Start date',
};

/** Fields only one column may fill. Description and note may take several. */
export const ONLY_ONE: Field[] = ['title', 'status', 'priority', 'kind', 'size', 'epic', 'sprint', 'assignee', 'responsible', 'blocked', 'reference', 'due', 'start'];

const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

/**
 * Headings we have seen, and what they turned out to be.
 *
 * Written as whole headings rather than substrings on purpose: "item type" is
 * a kind and "item id" is a reference, and a substring rule that looks for
 * "item" gets both wrong. Anything not here becomes a question.
 */
const HEADINGS: [Field, string[]][] = [
  ['title', ['name', 'title', 'task', 'task name', 'item', 'item name', 'work item', 'subject', 'headline', 'story', 'summary']],
  ['description', ['details', 'detail', 'description', 'notes', 'body', 'what', 'requirement', 'acceptance criteria']],
  ['status', ['status', 'state', 'progress', 'stage', 'item status', 'current status']],
  ['priority', ['priority', 'urgency', 'importance', 'severity']],
  ['kind', ['type', 'item type', 'work type', 'category', 'kind', 'issue type']],
  ['size', ['difficulty', 'effort', 'size', 'points', 'story points', 'estimate', 'complexity', 'days', 'est days']],
  ['epic', ['epic', 'theme', 'area', 'module', 'feature', 'component', 'group', 'workstream', 'project']],
  ['sprint', ['sprint', 'sprint assigned', 'iteration', 'release', 'version', 'milestone', 'cycle']],
  // The person ACCOUNTABLE for the work (RACI). The doer words — "responsible",
  // "developer" — went to their own field below, where the doer belongs.
  ['assignee', ['accountable', 'assigned to', 'assignee', 'owner', 'assigned']],
  // The person DOING the work (RACI Responsible). A file that names a developer
  // or a doer column lands here, not on the accountable field above.
  ['responsible', ['responsible', 'doer', 'developer', 'dev']],
  ['labels', ['labels', 'label', 'tags', 'tag']],
  ['blocked', ['blocked by', 'blocker', 'waiting on', 'dependency', 'depends on']],
  ['reference', ['item id', 'id', 'ref', 'reference', 'key', 'ticket', 'number', 'no', 'item no', 'item number']],
  ['due', ['due', 'due date', 'deadline', 'target date', 'end date', 'finish date', 'completion date', 'date required', 'required by', 'needed by']],
  ['start', ['start', 'start date', 'begin', 'begins', 'begin date', 'begin on', 'start on', 'planned start', 'kickoff', 'kick off', 'commence', 'commences']],
];

/**
 * What a heading probably is.
 *
 * `guess` rather than `exact` throughout: a heading is never literally a
 * FlightDeck field name, so claiming certainty about one would be a lie. The
 * screen shows every column either way — see the note on option A.
 */
export function guessColumn(heading: string): { to: Destination; sure: Sureness } {
  const n = norm(heading);
  for (const [field, names] of HEADINGS) if (names.includes(n)) return { to: field, sure: 'guess' };
  /*
   * A HEADING WE DO NOT KNOW BECOMES A NOTE, NOT A BLANK.
   *
   * Caught by running this over TRINRE's own file: "Comments" and "Date
   * Requested" matched nothing, came back blank, and blank contributed nothing
   * — so both columns vanished from every card without a word. That is exactly
   * what "we never drop them" forbids. The safe default is to keep it in the
   * description under its own heading; `sure: ''` still marks it as something
   * the person should look at.
   */
  return { to: 'note', sure: '' };
}

/**
 * The first pass over a file's headings.
 *
 * Two headings can both look like the title — "Name" and "Summary" both do —
 * and only one column may be the title. The first wins and the rest are asked
 * about, rather than the last quietly overwriting the first.
 */
export function guessColumns(headings: string[]): { to: Destination; sure: Sureness }[] {
  const taken = new Set<Field>();
  return headings.map((h) => {
    const g = guessColumn(h);
    if (g.to && ONLY_ONE.includes(g.to as Field)) {
      if (taken.has(g.to as Field)) return { to: 'note' as Destination, sure: '' as Sureness };
      taken.add(g.to as Field);
    }
    return g;
  });
}

/* ── The words inside the columns ─────────────────────────────────────────── */

/** exact: the same word FlightDeck uses. guess: somebody else's word for it. */
const STATUS_WORDS: [IssueStatus, Sureness, string[]][] = [
  ['backlog',    'exact', ['backlog']],
  ['backlog',    'guess', ['not started', 'new', 'open', 'pending', 'raised', 'logged', 'unstarted', 'proposed', 'idea']],
  ['todo',       'exact', ['to do', 'todo']],
  ['todo',       'guess', ['ready', 'next', 'planned', 'scheduled', 'approved', 'up next', 'selected']],
  ['inprogress', 'exact', ['in progress', 'inprogress']],
  ['inprogress', 'guess', ['doing', 'wip', 'started', 'active', 'in development', 'development', 'building', 'under way', 'underway']],
  ['staging',    'exact', ['in staging', 'staging']],
  ['staging',    'guess', ['staged', 'on staging', 'in staging env', 'awaiting deploy', 'ready to deploy', 'ready for staging', 'pre-prod', 'preprod']],
  ['inreview',   'exact', ['in review', 'inreview']],
  ['inreview',   'guess', ['review', 'testing', 'in test', 'in testing', 'in uat', 'uat', 'qa', 'in qa', 'completed development',
                           'ready for test', 'ready to test', 'verifying', 'awaiting sign off', 'awaiting approval', 'peer review']],
  ['done',       'exact', ['done']],
  ['done',       'guess', ['complete', 'completed', 'closed', 'finished', 'shipped', 'released', 'live', 'in live', 'in production', 'deployed', 'resolved', 'fixed']],
  ['wontdo',     'exact', ['wont do', 'won t do']],
  ['wontdo',     'guess', ['cancelled', 'canceled', 'rejected', 'abandoned', 'duplicate', 'declined', 'not doing', 'descoped', 'out of scope']],
];

const PRIORITY_WORDS: [IssuePriority, Sureness, string[]][] = [
  ['critical', 'exact', ['critical']], ['critical', 'guess', ['urgent', 'blocker', 'p0', '1', 'highest', 'must']],
  ['high',     'exact', ['high']],     ['high',     'guess', ['p1', '2', 'important', 'should']],
  ['medium',   'exact', ['medium']],   ['medium',   'guess', ['normal', 'moderate', 'p2', '3', 'standard', 'could']],
  ['low',      'exact', ['low']],      ['low',      'guess', ['minor', 'p3', '4', 'trivial', 'nice to have', 'wont']],
];

const KIND_WORDS: [IssueKind, Sureness, string[]][] = [
  ['bug',  'exact', ['bug']],
  ['bug',  'guess', ['defect', 'fault', 'fix', 'incident', 'problem', 'error', 'issue', 'hotfix']],
  ['task', 'exact', ['task']],
  ['task', 'guess', ['new feature', 'feature', 'improvement', 'enhancement', 'general work', 'work', 'story', 'chore', 'change', 'request', 'spike', 'admin']],
];

const SIZE_WORDS: [IssueSize, Sureness, string[]][] = [
  ['quick',  'exact', ['quick']],  ['quick',  'guess', ['small', 's', 'xs', 'trivial', 'tiny', 'hours']],
  ['medium', 'exact', ['medium']], ['medium', 'guess', ['m', 'few days', 'a few days', 'moderate']],
  ['long',   'exact', ['long']],   ['long',   'guess', ['large', 'l', 'xl', 'big', 'epic', 'weeks', 'a week or more']],
];

const TABLES: Partial<Record<Field, [string, Sureness, string[]][]>> = {
  status: STATUS_WORDS as [string, Sureness, string[]][],
  priority: PRIORITY_WORDS as [string, Sureness, string[]][],
  kind: KIND_WORDS as [string, Sureness, string[]][],
  size: SIZE_WORDS as [string, Sureness, string[]][],
};

/**
 * What one of their words means, if we can tell.
 *
 * DELIBERATELY BLANK FOR "BLOCKED" AND ITS SYNONYMS. Their sheet writes Blocked
 * where a status goes; FlightDeck keeps blocked separately, so a blocked card
 * still has a real status — To do, In progress, whatever it was. There is no
 * honest automatic answer to "which status is Blocked", so it is asked. Guessing
 * one would bury five real pieces of work under a status nobody chose.
 */
export const BLOCKED_WORDS = ['blocked', 'on hold', 'hold', 'waiting', 'stuck', 'impeded', 'paused'];

export function guessValue(field: Field, raw: string): { to: string; sure: Sureness } {
  const n = norm(raw);
  if (!n) return { to: '', sure: '' };
  const table = TABLES[field];
  if (!table) return { to: '', sure: '' };
  if (field === 'status' && BLOCKED_WORDS.includes(n)) return { to: '', sure: '' };
  for (const want of ['exact', 'guess'] as Sureness[]) {
    for (const [to, sure, words] of table) if (sure === want && words.includes(n)) return { to, sure };
  }
  return { to: '', sure: '' };
}

/** A board's own types, as much of one as the importer needs to match against. */
export type ImportType = { slug: string; name: string };

/**
 * Their word for a card's type → one of THIS board's types, if we can tell.
 *
 * Kind is unlike status or priority: its allowed values are not a fixed list in
 * the code, they are whatever types the board carries — which since #302 is
 * Feature, Bug, Tech debt and Maintenance by default, renamed or added to as the
 * board's owner sees fit. So the match is made against the board:
 *
 *   1. their word IS one of the board's types, by slug or by name — "Feature",
 *      "Tech debt", or a custom "Spike" all land on themselves;
 *   2. failing that, one of our built-in synonyms (KIND_WORDS) reads it as a
 *      type the board actually has — "Defect" → Bug, but only where Bug exists;
 *   3. failing both, we cannot tell, and it is asked.
 *
 * A synonym that resolves to a type the board does NOT have (the old default
 * 'task' on a board made since #302) is deliberately treated as unknown rather
 * than forced in — forcing it is exactly the foreign-key failure this fixes.
 */
export function guessKind(raw: string, types: ImportType[]): { to: string; sure: Sureness } {
  const n = norm(raw);
  if (!n) return { to: '', sure: '' };
  for (const t of types) if (norm(t.slug) === n || norm(t.name) === n) return { to: t.slug, sure: 'exact' };
  const g = guessValue('kind', raw);
  if (g.to && types.some((t) => t.slug === g.to)) return { to: g.to, sure: 'guess' };
  return { to: '', sure: '' };
}

/* ── Numbers ──────────────────────────────────────────────────────────────── */

/** True when every value in a column is a number — a difficulty score, not a word. */
export function isNumeric(values: string[]): boolean {
  const real = values.filter((v) => v.trim() !== '');
  return real.length > 0 && real.every((v) => /^-?\d+(\.\d+)?$/.test(v.trim()));
}

/**
 * Cutting a numeric column into the three sizes FlightDeck has.
 *
 * TRINRE score difficulty 1 to 10 and use nine of those numbers. Asking "what
 * is a 4" nine times is a form, not a mapping, so two numbers cut the range
 * instead: everything up to `quickUpTo` is Quick, everything above `longFrom`
 * is Long, the rest is in between.
 */
export function bandFor(n: number, quickUpTo: number, longFrom: number): IssueSize {
  if (n <= quickUpTo) return 'quick';
  if (n >= longFrom) return 'long';
  return 'medium';
}

/**
 * A sensible first cut at the two numbers, from the values actually present.
 *
 * Thirds of the RANGE, not of the rows. Thirds of the rows would move the
 * boundary every time the file changed and would call the easiest third of an
 * easy backlog "Long".
 */
export function suggestBands(values: string[]): { quickUpTo: number; longFrom: number } {
  const ns = values.map((v) => Number(v)).filter((n) => Number.isFinite(n));
  if (!ns.length) return { quickUpTo: 1, longFrom: 3 };
  const lo = Math.min(...ns);
  const hi = Math.max(...ns);
  if (hi === lo) return { quickUpTo: lo, longFrom: lo + 1 };
  const third = (hi - lo) / 3;
  return { quickUpTo: Math.floor(lo + third), longFrom: Math.ceil(hi - third) };
}

/* ── Building the cards ───────────────────────────────────────────────────── */

import type { Sheet } from './import-csv';

export type Plan = {
  /** One destination per heading, in the file's own order. */
  columns: Destination[];
  /** `${field}:${their word}` → our word. 'skip' leaves that row out entirely. */
  values: Record<string, string>;
  /** Where the two cuts sit, when a size column holds numbers. */
  bands: { quickUpTo: number; longFrom: number };
  /**
   * What a row whose status reads "Blocked" becomes.
   *
   * No default. FlightDeck cannot know whether their blocked work had been
   * started, and choosing for them is how five real pieces of work end up
   * somewhere nobody put them.
   */
  blockedStatus: IssueStatus | '';
  /**
   * WHAT A ROW WITH NO TYPE BECOMES — the same shape as blockedStatus, and for
   * the same reason. A board's types are its own (since #302 the four are
   * Feature, Bug, Tech debt, Maintenance, and a board may have renamed or added
   * to them), so there is no honest kind to assume for a row whose Type column
   * is empty, or that has no Type column at all. It is asked, against this
   * board's own types, and nothing is imported until it is answered.
   *
   * Absent means the importer was given no board types and falls back to its old
   * behaviour — see buildImport's `types` argument.
   */
  defaultKind?: IssueKind | '';
  /**
   * WHICH WAY THIS FILE'S NUMERIC DATES ARE READ — day-, month- or year-first.
   * A file writes its dates one way throughout, so this settles the whole import
   * rather than each cell being guessed. Absent means day-first, what this
   * product reads in. Word and ISO dates ignore it; only a bare numeric date
   * (03/04/2026) turns on it. See `guessDateOrder` / `parseImportDate`.
   */
  dateOrder?: DateOrder;
};

/** A card as it will be created. Everything the board fills in itself is absent. */
export type Draft = {
  title: string;
  description: string;
  status: IssueStatus;
  priority: IssuePriority;
  kind: IssueKind;
  size?: IssueSize;
  epic?: string;
  sprintName?: string;
  assignee?: string;
  responsible?: string;
  labels?: string[];
  blocked?: string;
  blockerTarget?: string;
  summary?: string;
  /** Their own reference, e.g. LH042. Kept for the description and for reporting. */
  reference?: string;
  /** ISO, parsed in the file's chosen date order. Absent when the cell was empty
   *  or could not be read as a date. */
  dueDate?: string;
  startsOn?: string;
  /** The line in the file this came from, so a problem can be pointed at. */
  line: number;
};

export type Problem = {
  kind: 'no-title' | 'unmapped-value' | 'repeated-reference' | 'ragged-row' | 'no-title-column' | 'blocked-unanswered' | 'untyped-unanswered';
  what: string;
  rows: number;
};

const has = (v: string | undefined) => !!v && v.trim() !== '';

/** "Blocked by LH070." → LH070. Their comments name what they are waiting on. */
function targetIn(text: string): string {
  const m = text.match(/\b([A-Z]{2,6}[- ]?\d{1,6}|#\d+)\b/);
  return m ? m[1].replace(/ /, '-') : '';
}

/**
 * Everything a file becomes, and everything wrong with it, in one pass.
 *
 * Returns the drafts AND the problems rather than throwing on the first fault:
 * a person about to import 78 rows needs to see all of what is wrong at once,
 * not be sent round the loop one row at a time.
 */
export function buildImport(sheet: Sheet, plan: Plan, types: ImportType[] = [], stagingEnabled = false): { drafts: Draft[]; sprints: string[]; problems: Problem[] } {
  const problems: Problem[] = [];
  const at = (f: Field) => plan.columns.indexOf(f);
  const iTitle = at('title');

  if (iTitle < 0) {
    problems.push({ kind: 'no-title-column', what: 'No column has been set as the title', rows: sheet.rows.length });
    return { drafts: [], sprints: [], problems };
  }
  if (sheet.ragged.length) {
    problems.push({ kind: 'ragged-row', what: 'rows had a different number of cells and were filled out', rows: sheet.ragged.length });
  }

  const unmapped = new Map<string, number>();
  const noTitle: number[] = [];
  const seenRef = new Map<string, number>();
  const repeated = new Set<string>();
  const sprints: string[] = [];
  const drafts: Draft[] = [];
  let blockedUnanswered = 0;
  // Rows the file gives no type — an empty Type cell, or no Type column at all —
  // when the board's types are known and no default has been chosen. Counted
  // like the blocked rows, and it stops the import the same way.
  const boardAware = types.length > 0;
  let untyped = 0;

  /** Their word → ours, remembering anything we had to ask about and were not told. */
  const mapped = (field: Field, raw: string): string => {
    const key = `${field}:${raw}`;
    const said = plan.values[key];
    if (said !== undefined) return said;
    const g = guessValue(field, raw);
    if (!g.to) unmapped.set(key, (unmapped.get(key) ?? 0) + 1);
    return g.to;
  };

  sheet.rows.forEach((row, i) => {
    const line = i + 2;
    const cell = (f: Field) => { const c = at(f); return c < 0 ? '' : (row[c] ?? '').trim(); };

    const title = (row[iTitle] ?? '').trim();
    if (!title) { noTitle.push(line); return; }

    // Description: the description column, then every column kept as a note,
    // each under its own heading so nothing arrives without saying what it is.
    /*
     * THE BODY FIRST, THE KEPT COLUMNS UNDERNEATH — not the file's own order.
     *
     * TRINRE's Date Requested is column B and the requirement is column G, so
     * following the file put "**Date Requested:** 2026-07-09" above the thing
     * the card is actually about, on all 78 cards.
     */
    const body: string[] = [];
    const kept: string[] = [];
    const notes: string[] = [];
    plan.columns.forEach((to, c) => {
      const v = (row[c] ?? '').trim();
      if (!v) return;
      if (to === 'description') body.push(v);
      else if (to === 'note') { kept.push(`**${sheet.headers[c]}:** ${v}`); notes.push(v); }
      /*
       * A COLUMN THAT IS USED SOMEWHERE ELSE IS STILL KEPT IN THE DESCRIPTION.
       *
       * Both of these are read into a field of their own AND written down, and
       * both are here because leaving them out lost them completely:
       *
       *   reference — used as the card number, so it looks redundant. It is not:
       *     choosing FlightDeck's numbering instead silently threw their Item Id
       *     away while the screen said their references were kept.
       *
       *   sprint — found by Vinnie at step 5.6 of the walk, 20 August 2026.
       *     No sprints are created on import (the file says which, never when),
       *     so the sprint name had nowhere to go and vanished from all 78 cards
       *     — while step 4.8 promised, in as many words, that "each card keeps
       *     the one it was in, in its description".
       */
      else if (to === 'reference' || to === 'sprint') kept.push(`**${sheet.headers[c]}:** ${v}`);
    });
    const parts = [...body, ...kept];

    const rawStatus = cell('status');
    const isBlocked = BLOCKED_WORDS.includes(norm(rawStatus));
    let status = (isBlocked ? plan.blockedStatus : mapped('status', rawStatus) as IssueStatus) || '';
    if (isBlocked && !plan.blockedStatus) blockedUnanswered += 1;
    /* In Staging only lands on a board that turned the column on. Everywhere else
       the word is one we could not place — recorded like any unrecognised value so
       the person maps it to a real column, rather than a card arriving in a lane
       the board does not show. */
    if (status === 'staging' && !stagingEnabled) {
      unmapped.set(`status:${rawStatus}`, (unmapped.get(`status:${rawStatus}`) ?? 0) + 1);
      status = '' as IssueStatus;
    }
    if (!status) status = 'backlog';
    if (status === 'skip' as IssueStatus) return;

    const priority = (mapped('priority', cell('priority')) as IssuePriority) || 'medium';
    /*
     * THE CARD'S TYPE, AGAINST THIS BOARD'S OWN TYPES — the fix for the
     * foreign-key failure a board made since #302 met on every import. The old
     * line here defaulted to 'task', a type such a board does not have.
     *
     * When the board's types are known (the app always passes them; some tests
     * do not), a value is matched with guessKind and, if that cannot tell, asked
     * — recorded in `unmapped` exactly as an unrecognised status is. A row with
     * no type at all takes plan.defaultKind, and if none has been chosen it is
     * counted and the import waits. The placeholder below never reaches the
     * board: an unanswered kind stops the import, the same as an unanswered
     * blocked status.
     */
    let kind: IssueKind;
    if (boardAware) {
      const rawKind = cell('kind');
      if (has(rawKind)) {
        const said = plan.values[`kind:${rawKind}`];
        kind = (said !== undefined ? said : guessKind(rawKind, types).to) as IssueKind;
        if (!kind) unmapped.set(`kind:${rawKind}`, (unmapped.get(`kind:${rawKind}`) ?? 0) + 1);
      } else {
        kind = (plan.defaultKind || '') as IssueKind;
        if (!kind) untyped += 1;
      }
      if (!kind) kind = (types[0]?.slug ?? '') as IssueKind; // preview only; import is gated
    } else {
      kind = (mapped('kind', cell('kind')) as IssueKind) || 'task';
    }

    // Size: numbers go through the bands, words through the table.
    const rawSize = cell('size');
    let size: IssueSize | undefined;
    if (has(rawSize)) {
      size = /^-?\d+(\.\d+)?$/.test(rawSize)
        ? bandFor(Number(rawSize), plan.bands.quickUpTo, plan.bands.longFrom)
        : ((mapped('size', rawSize) as IssueSize) || undefined);
    }

    /*
     * THE REASON IT IS BLOCKED — their blocked column if they have one, else
     * the NOTES on a row whose status said Blocked.
     *
     * Notes, not the description. TRINRE write "Blocked by LH070" in Comments
     * while Details holds the requirement; taking the description gave every
     * blocked card a 200-word reason that never said what it was waiting on.
     * Where more than one note exists, the one naming another item wins.
     */
    const reasonCol = cell('blocked');
    /*
     * A DATE IS NOT A REASON. Taking the first note gave LH003 a reason of
     * "2026-07-03" — its Date Requested — while "Require sample template" sat
     * in the next column. A reason has words in it, so only notes with words
     * are candidates, and among those the one naming another item wins.
     */
    const wordy = notes.filter((n) => /[a-z]{3}/i.test(n));
    const reason = has(reasonCol) ? reasonCol
      : (isBlocked ? (wordy.find((n) => !!targetIn(n)) ?? wordy[wordy.length - 1] ?? '') : '');

    const reference = cell('reference');
    if (has(reference)) {
      if (seenRef.has(reference)) repeated.add(reference);
      else seenRef.set(reference, line);
    }

    const sprintName = cell('sprint');
    // "Unassigned" is their word for the backlog, not the name of a sprint.
    const realSprint = has(sprintName) && !['unassigned', 'none', 'backlog', 'tbd', 'n a'].includes(norm(sprintName)) ? sprintName : '';
    if (realSprint && !sprints.includes(realSprint)) sprints.push(realSprint);

    const labels = cell('labels');

    /*
     * DATES, READ IN THE ONE ORDER CHOSEN FOR THE FILE — #423. A cell that is
     * empty or is not a date returns nothing, so the card arrives without that
     * date rather than with a wrong one; `dateOrder` (absent = day-first) is
     * what settles 03/04 either way. A date column is read into its field and,
     * unlike reference and sprint, is NOT also kept in the description — it has
     * a home now, and "**Due date:** 2026-07-09" over the requirement is the
     * clutter this replaces.
     */
    const order: DateOrder = plan.dateOrder ?? 'dmy';
    const dueDate = parseImportDate(cell('due'), order);
    const startsOn = parseImportDate(cell('start'), order);

    drafts.push({
      title,
      description: parts.join('\n\n'),
      status: status as IssueStatus,
      priority, kind, line,
      ...(size ? { size } : {}),
      ...(has(cell('epic')) ? { epic: cell('epic') } : {}),
      ...(realSprint ? { sprintName: realSprint } : {}),
      ...(has(cell('assignee')) ? { assignee: cell('assignee') } : {}),
      ...(has(cell('responsible')) ? { responsible: cell('responsible') } : {}),
      ...(has(labels) ? { labels: labels.split(/[,;]/).map((s) => s.trim()).filter(Boolean) } : {}),
      ...(has(reason) ? { blocked: reason.slice(0, 300) } : {}),
      ...(has(reason) && targetIn(reason) ? { blockerTarget: targetIn(reason) } : {}),
      ...(has(reference) ? { reference } : {}),
      ...(dueDate ? { dueDate } : {}),
      ...(startsOn ? { startsOn } : {}),
    });
  });

  if (noTitle.length) problems.push({ kind: 'no-title', what: 'rows had nothing in the title column and were left out', rows: noTitle.length });
  for (const [key, n] of unmapped) problems.push({ kind: 'unmapped-value', what: key, rows: n });
  for (const ref of repeated) problems.push({ kind: 'repeated-reference', what: ref, rows: sheet.rows.filter((r) => (r[at('reference')] ?? '').trim() === ref).length });
  if (blockedUnanswered) problems.push({ kind: 'blocked-unanswered', what: 'rows say Blocked, which is not a FlightDeck status', rows: blockedUnanswered });
  if (untyped) problems.push({ kind: 'untyped-unanswered', what: 'rows have no type, and no default has been chosen', rows: untyped });

  return { drafts, sprints, problems };
}

/* ── Their numbering, kept ────────────────────────────────────────────────── */

/**
 * A board's own way of numbering cards.
 *
 * VINNIE'S DECISION, 19 August 2026, when told three of TRINRE's Item Ids were
 * used twice and their reference therefore could not be the card number:
 *
 *     "Use the numbering system that sheet has throughout the project. Because
 *      they would have started their own. They may want to continue using their
 *      own in accordance with what was already approved by external sign-off
 *      documents."
 *
 * That is the right call and it is worth writing down why: their numbers are
 * not a convenience, they are what a signed document points at. A requirements
 * document approved by a project owner says LH042; renumbering it to #310 on
 * the way in makes every signed document in the project wrong about what it
 * approved. So the numbering comes with them and continues from where their
 * sheet left off — which their own file states: "Next Item No: LH077".
 */
export type Scheme = { prefix: string; width: number; next: number };

/** '#' and no padding — what a board that has not imported anything uses. */
export const HOUSE_SCHEME: Scheme = { prefix: '#', width: 0, next: 1 };

const PARTS = /^([A-Za-z][A-Za-z0-9_-]*?)[- ]?(\d+)$/;

/**
 * Work out the pattern from the references a file actually uses.
 *
 * Returns null unless MOST of them agree — one stray "TBD" in a column of
 * LH001s should not stop the scheme being recognised, but a column of free text
 * is not a numbering scheme and pretending otherwise would give every card a
 * reference made up out of nothing.
 */
export function schemeFrom(refs: string[]): Scheme | null {
  const seen = refs.map((r) => r.trim()).filter(Boolean);
  if (seen.length < 2) return null;

  const parsed = seen.map((r) => PARTS.exec(r)).filter((m): m is RegExpExecArray => !!m);
  if (parsed.length < seen.length * 0.8) return null;

  // One prefix, or it is not one scheme.
  const prefixes = new Set(parsed.map((m) => m[1].toUpperCase()));
  if (prefixes.size !== 1) return null;

  const prefix = parsed[0][1];
  // The padding they use, so LH077 comes out as LH077 and not LH77.
  const width = Math.max(...parsed.map((m) => m[2].length));
  const highest = Math.max(...parsed.map((m) => Number(m[2])));
  return { prefix, width, next: highest + 1 };
}

/**
 * A bug's reference carries a "B" that is part of its IDENTITY, not a swappable
 * prefix: a board numbers bugs `B1`, `B2`… in their own sequence, plainly, and
 * under a project key they read `CSM-B1`. The "B" sits between any scheme prefix
 * and the number, so it survives a change of scheme where a bare integer would
 * lose it (see setNumbering in store.ts, and the round-trip test).
 *
 * It is recognised only in the two forms this app writes: the plain `B4`, and a
 * key scheme's `KEY-B4` where the separator before the "B" tells it apart from
 * an imported prefix that merely ends in a letter (`LH077` is not a bug). A key
 * scheme always writes that separator, so an ordinary `KEY-4` can never be read
 * as one.
 */
const BUG_REF = /(?:^|[-\s])B(\d+)\s*$/;

/** Whether a reference is a bug's — a "B" marking the number, in either scheme. */
export function isBugRef(ref: string): boolean {
  return BUG_REF.test(ref.trim());
}

/**
 * LH + 77 + width 3 → "LH077". A bug keeps its "B" between the prefix and the
 * number: plain `B4`, project key `FDD-B4`. Under the plain '#' scheme the "B"
 * IS the marker, so it stands in place of the '#' rather than being added to it.
 */
export function refFor(scheme: Scheme, n: number, isBug = false): string {
  const num = `${isBug ? 'B' : ''}${String(n).padStart(scheme.width, '0')}`;
  const prefix = isBug && scheme.prefix === '#' ? '' : scheme.prefix;
  return `${prefix}${num}`;
}

/**
 * The number a reference is built around, whatever scheme wrote it — "#267" →
 * 267, "CSM-001" → 1, "LH077" → 77, "FDD-B4" → 4.
 *
 * The number is what stays fixed when a board changes how it labels its issues
 * (see setNumbering in store.ts): the label is re-made from this, so the card a
 * signed document points at is still the same card. It is the trailing run of
 * digits, because every scheme this app writes is a prefix followed by the
 * zero-padded number and nothing after it. A bug's "B" is preserved separately
 * (see tokenFromRef / isBugRef), so the number alone never carries it.
 */
export function numberFromRef(ref: string): number | null {
  const m = /(\d+)\s*$/.exec(ref);
  return m ? parseInt(m[1], 10) : null;
}

/**
 * The whole identity TOKEN a reference is built around, prefix stripped: `4` for
 * an ordinary card, `B4` for a bug. This is what a scheme prefixes and what must
 * be carried across a scheme change intact — rebuilding from the bare number
 * alone silently drops a bug's "B" and can collide a bug with the ordinary card
 * of the same number. Reverse of refFor: refFor(scheme, n, isBug) then reads
 * back to the same token whatever the scheme.
 */
export function tokenFromRef(ref: string): string | null {
  const n = numberFromRef(ref);
  if (n == null) return null;
  return isBugRef(ref) ? `B${n}` : String(n);
}

export type Renumbered = { was: string; now: string; title: string; line: number };

/**
 * Give every card its reference, keeping theirs wherever it can be kept.
 *
 * Two cards cannot wear one reference — that is what broke the last time a
 * counter was read instead of taken (see createIssue in store.ts) — so where
 * their sheet uses an id twice, the FIRST row keeps it and the later one is
 * given the next free number in their own scheme. It is reported by name, not
 * absorbed: somebody reconciling a signed document needs to be told that the
 * second LH010 is now LH077, and which one it was.
 */
export function assignRefs(
  drafts: Draft[],
  scheme: Scheme,
  /*
   * WHETHER THEIR OWN REFERENCES ARE USED AT ALL.
   *
   * False means the board numbers everything itself and their references are
   * kept only in the descriptions. Found by unticking the box on the built
   * screen: without this, "use our numbering" still handed most cards their
   * LH-number and renumbered only the three that collided — a board numbered
   * LH001, LH002, #1, LH004, which is neither scheme.
   */
  useTheirs = true,
): { refs: string[]; renumbered: Renumbered[]; scheme: Scheme } {
  const taken = new Set<string>();
  const renumbered: Renumbered[] = [];
  let next = scheme.next;
  const refs = drafts.map((d) => {
    const theirs = useTheirs ? (d.reference ?? '').trim() : '';
    if (theirs && !taken.has(theirs.toUpperCase())) {
      taken.add(theirs.toUpperCase());
      return theirs;
    }
    // A bug given a fresh number on the way in carries the 'B' token the same as
    // one raised in the app (createIssue) — so import and native creation agree.
    const bug = d.kind === 'bug';
    let fresh = refFor(scheme, next, bug);
    while (taken.has(fresh.toUpperCase())) { next += 1; fresh = refFor(scheme, next, bug); }
    taken.add(fresh.toUpperCase());
    next += 1;
    if (theirs) renumbered.push({ was: theirs, now: fresh, title: d.title, line: d.line });
    return fresh;
  });
  return { refs, renumbered, scheme: { ...scheme, next } };
}

/**
 * WHICH NUMBERING A BUILT IMPORT USES.
 *
 * Three ways numbers can run, and the person chooses between whichever apply:
 *   'keep'  — the sheet's OWN numbers (only meaningful when the sheet has some);
 *             a signed document points at LH042 and must go on doing so.
 *   'board' — the board's existing prefix (when it has one), numbered afresh —
 *             for a sheet transferred onto a board that already uses a scheme.
 *   'house' — plain '#'.
 * A choice that names a scheme that is not there falls back to the house '#', so
 * a bad combination can never mint a reference out of nothing.
 *
 * The rule this serves (Vinnie, 10–11 Sep): the numbering is always shown and
 * verified, never applied silently — a numberless sheet asks, a board's prefix
 * wins by default, and a sheet whose own prefix differs from the board's is put
 * in front of the person to reconcile rather than mixed in unnoticed.
 */
export type NumberChoice = 'keep' | 'board' | 'house';

export function pickScheme(opts: {
  sheet: Scheme | null;
  board: Scheme | null;
  house: Scheme;
  choice: NumberChoice;
}): { scheme: Scheme; useTheirs: boolean } {
  if (opts.choice === 'keep' && opts.sheet) return { scheme: opts.sheet, useTheirs: true };
  if (opts.choice === 'board' && opts.board) return { scheme: opts.board, useTheirs: false };
  // House, or a choice whose scheme is absent. Only a sheet's OWN references are
  // ever reused, and the house never reuses them.
  return { scheme: opts.house, useTheirs: false };
}

/**
 * Where the numbering choice starts before the person touches it: the sheet's
 * own numbers if it brought any, else the board's prefix (the board wins for a
 * numberless sheet), else the house '#'. It is a starting point shown for
 * verification, not a silent decision.
 */
export function defaultChoice(sheet: Scheme | null, board: Scheme | null): NumberChoice {
  if (sheet) return 'keep';
  if (board) return 'board';
  return 'house';
}
